import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/shared/hooks/useAuth';
import { useVertical } from '@/shared/hooks/useVertical';
import { useToast } from '@/shared/ui/Toast';
import { Button, Callout } from '@/shared/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { uploadApi, type MarketIntelData, type CensusZip } from '../api/uploadApi';
import { strategyKeys } from '../queryKeys';
import { Card } from '@/shared/ui/Card';
import { Badge } from '@/shared/ui/Badge';
import { SectionLabel } from '@/shared/ui/SectionLabel';
import { Stat } from '@/shared/ui/Stat';
import type { StatTone } from '@/shared/ui/Stat';
import styles from './MarketIntelligenceStep.module.css';

const ADA_AVG_SPEND = '$945–$1K'; // ADA HPI national benchmark — dental only

type SortKey = 'zip' | 'population' | 'medianIncome' | 'pctUninsured' | 'share' | 'score';
type SortDir = 'asc' | 'desc';

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

function fmtDollar(n: number): string {
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

function fmtPct(n: number): string {
  return `${n.toFixed(1)}%`;
}

// Build the zip code rows by joining census data with geographic (patient) data
function buildZipRows(intel: MarketIntelData) {
  const censusMap = new Map<string, CensusZip>();
  for (const z of intel.census?.zipCodes ?? []) {
    censusMap.set(z.zip, z);
  }

  const geoMap = new Map<string, number>();
  for (const z of intel.geographic?.zipCodes ?? []) {
    geoMap.set(z.zip, z.patientCount);
  }

  const allZips = intel.census?.zipCodes.map((z) => z.zip) ?? [];

  return allZips.map((zip) => {
    const census = censusMap.get(zip);
    const patientCount = geoMap.get(zip);
    const detail = census?.detailedData;
    const pop = detail?.supplemental?.totalPopulation ?? null;
    // Same care-seeking definition as the market share (and the scores).
    const zipCareSeeking = pop != null ? pop * (intel.seekingRate ?? 0) : null;
    const share =
      patientCount != null && zipCareSeeking ? (patientCount / zipCareSeeking) * 100 : null;

    return {
      zip,
      city: census?.city ?? '—',
      population: pop,
      // 0 = suppressed in older cached rows; show it as unknown, not $0.
      medianIncome: detail?.income?.medianHouseholdIncome || null,
      pctHispanic: detail?.supplemental?.raceHispanic ?? null,
      pctUninsured: detail?.healthInsurance?.uninsuredRate ?? null,
      patientCount: patientCount ?? null,
      share,
    };
  });
}

// Compute blended census gender split (population-weighted across all zips)
function computeCensusGender(zipCodes: CensusZip[]): { malePct: number; femalePct: number } | null {
  let totalPop = 0;
  let totalMale = 0;
  for (const z of zipCodes) {
    const d = z.detailedData;
    if (!d?.gender) continue;
    const pop = d.supplemental.totalPopulation;
    totalPop += pop;
    totalMale += pop * (d.gender.malePct / 100);
  }
  if (totalPop === 0) return null;
  const malePct = (totalMale / totalPop) * 100;
  return { malePct, femalePct: 100 - malePct };
}

export function MarketIntelligenceStep() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();

  const {
    data: intelRes,
    isLoading,
    isFetching,
    isError: intelError,
    refetch: refetchIntel,
  } = useQuery({
    queryKey: strategyKeys.marketIntel(locationId),
    queryFn: () => uploadApi.getMarketIntel(locationId),
    enabled: !!locationId,
  });

  const { data: enrichRes } = useQuery({
    queryKey: strategyKeys.enrichment(locationId),
    queryFn: () => uploadApi.getEnrichment(locationId),
    enabled: !!locationId,
  });

  const { data: radiusRes } = useQuery({
    queryKey: strategyKeys.radius(locationId),
    queryFn: () => uploadApi.getRadius(locationId),
    enabled: !!locationId,
  });
  // isSpecialist comes from the backend so the client doesn't need to know each
  // vertical's generalist specialty value.
  const isSpecialist = radiusRes?.data?.isSpecialist ?? false;
  const { user } = useAuth();
  const { verticalId } = useVertical();
  const { showToast } = useToast();
  // Viewers get a 403 from PUT /radius; don't offer them a slider that fails.
  const canEditRadius = user?.role !== 'viewer';
  const maxRadius = isSpecialist ? 60 : 20;
  const radius = intelRes?.data?.radius ?? (isSpecialist ? 20 : 5);
  const [pendingRadius, setPendingRadius] = useState<number | null>(null);
  const displayRadius = pendingRadius ?? radius;

  // Clear pending once refetched data matches
  if (pendingRadius != null && pendingRadius === radius) {
    setPendingRadius(null);
  }

  const radiusTimer = useRef<ReturnType<typeof setTimeout>>();
  // Don't fire a pending radius save after leaving the page or switching location.
  useEffect(() => () => clearTimeout(radiusTimer.current), [locationId]);
  const radiusMutation = useMutation({
    mutationFn: (r: number) => uploadApi.setRadius(locationId, r),
    onError: (err: unknown) => {
      setPendingRadius(null);
      showToast((err as Error).message || "Couldn't change the radius.", 'error');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: strategyKeys.marketIntel(locationId) });
      queryClient.invalidateQueries({ queryKey: strategyKeys.radius(locationId) });
      // Also invalidate competition density so navigating to Competition step refetches
      queryClient.invalidateQueries({ queryKey: strategyKeys.competitorsDensity(locationId) });
    },
  });
  const commitRadius = (val: number) => {
    clearTimeout(radiusTimer.current);
    radiusTimer.current = setTimeout(() => radiusMutation.mutate(val), 1000);
  };

  // Table sorting (hooks must be before any early return)
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  if (isLoading) return <div className={styles.loadingState}>Loading market intelligence...</div>;
  // A failed load used to render the page with every figure as "—", which
  // read as "no data for this area" rather than "try again".
  if (intelError && !intelRes) {
    return (
      <Callout
        variant="warning"
        title="We couldn't load your market data"
        actions={
          <Button size="sm" variant="secondary" onClick={() => void refetchIntel()}>
            Try again
          </Button>
        }
      >
        Census and patient figures for your area didn't load. Your inputs are safe — try again in a
        moment.
      </Callout>
    );
  }

  const intel = intelRes?.data;
  const enrichment = enrichRes?.data;
  const hasCensus = !!intel?.census;
  const hasGeo = !!intel?.geographic;
  const hasDemo = !!intel?.demographics;
  const hasFinancial = !!intel?.financial;

  const totalPopulation = intel?.census?.totalPopulation ?? 0;

  // Care-seeking: residents who seek this vertical's care in a year — the
  // backend's figure, so it matches the market share's denominator exactly.
  const careSeeking = hasCensus
    ? (intel?.marketShare?.careSeekingPopulation ??
      Math.round(totalPopulation * (intel?.seekingRate ?? 0)))
    : 0;
  // One definition, computed by the backend (patients in the area ÷ the
  // area's care-seeking population) — the Overview shows the same number.
  const marketShare = intel?.marketShare?.percent ?? null;
  const totalPatients =
    intel?.marketShare?.patients ??
    intel?.demographics?.totalPatients ??
    intel?.geographic?.totalPatients ??
    0;

  // Population-weighted, ignoring ZIPs whose income the Census suppresses.
  const blendedIncome = (() => {
    let weighted = 0;
    let pop = 0;
    for (const z of intel?.census?.zipCodes ?? []) {
      const income = z.detailedData?.income?.medianHouseholdIncome;
      const p = z.detailedData?.supplemental?.totalPopulation ?? 0;
      if (!income || p <= 0) continue;
      weighted += income * p;
      pop += p;
    }
    return pop > 0 ? weighted / pop : null;
  })();

  const unsortedRows = intel ? buildZipRows(intel) : [];

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const zipRows = [...unsortedRows].sort((a, b) => {
    const scoreA = intel?.scores?.[a.zip]?.score ?? -1;
    const scoreB = intel?.scores?.[b.zip]?.score ?? -1;
    const valMap: Record<SortKey, [number | string | null, number | string | null]> = {
      zip: [a.zip, b.zip],
      population: [a.population, b.population],
      medianIncome: [a.medianIncome, b.medianIncome],
      pctUninsured: [a.pctUninsured, b.pctUninsured],
      share: [a.share, b.share],
      score: [scoreA, scoreB],
    };
    const [va, vb] = valMap[sortKey];
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    const cmp = va < vb ? -1 : va > vb ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Census gender baseline (blended across service area)
  const censusGender = intel?.census ? computeCensusGender(intel.census.zipCodes) : null;

  // Practice gender skew (from PMS demographics upload)
  const genderSplit = intel?.demographics?.genderSplit;
  const totalGender = genderSplit ? genderSplit.male + genderSplit.female + genderSplit.other : 0;
  const practiceMalePct = totalGender > 0 ? (genderSplit!.male / totalGender) * 100 : null;
  const genderDelta =
    practiceMalePct != null && censusGender
      ? Math.round((practiceMalePct - censusGender.malePct) * 10) / 10
      : null;

  // Avg spend
  const avgSpend = intel?.financial?.avgSpend ?? null;
  const spendPeriods = intel?.financial?.timePeriods?.length ?? 0;

  // Score level → Badge variant
  const scoreLevelVariant = (level: string | undefined) => {
    if (level === 'HIGH') return 'primary' as const;
    if (level === 'MEDIUM') return 'warning' as const;
    return 'neutral' as const;
  };

  // Market share tone
  const marketShareTone: StatTone = marketShare != null ? 'warning' : 'muted';

  // Gender skew value class
  const genderValueClass = hasDemo
    ? styles.insightValueWarning
    : hasCensus
      ? styles.insightValueDefault
      : styles.insightValueDim;

  return (
    <div className={styles.root}>
      <div className={styles.headerWrap}>
        <div className={styles.headerRow}>
          <h3 className={styles.headerTitle}>Market Intelligence</h3>
          <div className={styles.headerControls}>
            <label className={styles.radiusLabel}>Radius</label>
            <input
              type="range"
              disabled={!canEditRadius}
              title={canEditRadius ? undefined : 'Only managers and owners can change the radius'}
              min={isSpecialist ? 5 : 2}
              max={maxRadius}
              value={displayRadius}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setPendingRadius(val);
                commitRadius(val);
              }}
              className={styles.radiusSlider}
            />
            <span className={styles.radiusValue}>{displayRadius} mi</span>
            {enrichment &&
              (enrichment.uploadCount === 0 ? (
                <Badge variant="warning">Basic Level — upload data for deeper insights</Badge>
              ) : (
                <Badge variant="primary">PMS Data Enriched</Badge>
              ))}
          </div>
        </div>
        <p className={styles.headerSub}>
          {`Population & demographics within ~${displayRadius} miles (approx. driving distance)`}
        </p>
      </div>

      {/* Top stats — 4 columns matching mockup */}
      <Card className={styles.statsCard}>
        <div
          className={
            isFetching && !isLoading
              ? `${styles.statsGrid} ${styles.statsGridHidden}`
              : styles.statsGrid
          }
        >
          <Stat
            label="Service Area Pop."
            value={hasCensus ? fmt(totalPopulation) : '—'}
            sub={hasCensus ? `${intel!.census!.zipCodes.length} zip codes` : 'Needs census data'}
          />
          <Stat
            label="Care-Seeking"
            value={hasCensus ? fmt(careSeeking) : '—'}
            sub={
              hasCensus && intel?.seekingRate
                ? `~${Math.round(intel.seekingRate * 100)}% seek care yearly`
                : '—'
            }
            tone="accent"
          />
          <Stat
            label="Your Market Share"
            value={marketShare != null ? fmtPct(marketShare) : '—'}
            sub={
              totalPatients > 0
                ? `${totalPatients.toLocaleString()} patients${intel?.marketShare?.scope === 'area' ? ' in this area' : ''}`
                : 'Needs PMS data'
            }
            tone={marketShareTone}
          />
          <Stat
            label="Median HH Income"
            value={blendedIncome != null ? fmtDollar(blendedIncome) : '—'}
            sub={hasCensus ? 'Population-weighted' : 'Needs census data'}
            tone="info"
          />
        </div>
        {isFetching && !isLoading && (
          <div className={styles.fetchingOverlay}>
            <div className={styles.spinner} />
            <span className={styles.fetchingText}>Updating market intelligence...</span>
          </div>
        )}
      </Card>

      {intel?.censusError === 'partial' && (
        <Callout variant="warning">
          Census data is missing for {intel.missingZipCount} zip code
          {intel.missingZipCount === 1 ? '' : 's'} in this area, so population figures are
          understated. Try again shortly.
        </Callout>
      )}

      {/* Service Area Breakdown table */}
      <Card className={styles.breakdownCard}>
        <SectionLabel label="Service Area Breakdown" sub="Patient distribution vs. opportunity" />
        {zipRows.length === 0 ? (
          <div className={styles.emptyBreakdown}>
            {intel?.censusError === 'missing_zip'
              ? 'Your practice location is missing a zip code. Update it in your location settings to load market data.'
              : intel?.censusError === 'invalid_zip'
                ? "Your practice's zip code isn't a US zip code we can map. Check it in your location settings."
                : intel?.censusError === 'fetch_failed'
                  ? "Couldn't load census data for your service area. Please try again shortly, or contact support if this persists."
                  : 'No zip code data available yet.'}
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr className={styles.tableHeadRow}>
                {(
                  [
                    ['Zip Code', null],
                    ['Population', 'population'],
                    ['Med. Income', 'medianIncome'],
                    ['% Uninsured', 'pctUninsured'],
                    ['Your Share', 'share'],
                    [hasGeo ? 'Opportunity' : 'Market Score', 'score'],
                  ] as [string, SortKey | null][]
                ).map(([label, key]) => (
                  <th
                    key={label}
                    onClick={key ? () => toggleSort(key) : undefined}
                    className={key ? `${styles.th} ${styles.thSortable}` : styles.th}
                  >
                    {label}
                    {key && (
                      <span
                        className={
                          sortKey === key
                            ? `${styles.sortIndicator} ${styles.sortIndicatorActive}`
                            : styles.sortIndicator
                        }
                      >
                        {sortKey === key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {zipRows.map((r) => {
                const zipScore = intel?.scores?.[r.zip];
                return (
                  <tr key={r.zip} className={styles.tableRow}>
                    <td className={styles.tdZip}>
                      {r.zip}
                      {r.city !== '—' ? ` (${r.city})` : ''}
                    </td>
                    <td className={styles.tdMuted}>
                      {r.population != null ? r.population.toLocaleString() : '—'}
                    </td>
                    <td className={styles.tdMuted}>
                      {r.medianIncome != null ? fmtDollar(r.medianIncome) : '—'}
                    </td>
                    <td
                      className={
                        r.pctUninsured != null && r.pctUninsured > 10
                          ? styles.tdUninsuredHigh
                          : styles.tdUninsuredNormal
                      }
                    >
                      {r.pctUninsured != null ? fmtPct(r.pctUninsured) : '—'}
                    </td>
                    <td className={r.share != null ? styles.tdSharePresent : styles.tdShareMissing}>
                      {r.share != null ? fmtPct(r.share) : '—'}
                    </td>
                    <td className={styles.tdScore}>
                      {zipScore ? (
                        <Badge variant={scoreLevelVariant(zipScore.level)}>{zipScore.level}</Badge>
                      ) : (
                        <Badge variant="neutral">—</Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!hasGeo && zipRows.length > 0 && (
          <div className={styles.tableFootnote}>
            Market Score is based on population, income, insurance rates, proximity, and competitive
            density. Upload patient geographic data to see personalized Opportunity scores based on
            your actual market penetration. <span className={styles.uploadLink}>Upload now →</span>
          </div>
        )}
      </Card>

      {/* Insight cards — 2 columns matching mockup */}
      <div className={styles.insightGrid}>
        {/* Gender Skew */}
        <Card>
          <div className={styles.insightKicker}>Gender Skew</div>
          <div className={genderValueClass}>
            {practiceMalePct != null
              ? `${practiceMalePct.toFixed(1)}% male`
              : censusGender
                ? `${censusGender.malePct.toFixed(1)}% male`
                : '—'}
          </div>
          <div className={styles.insightSub}>
            {hasDemo && censusGender
              ? `Pop. is ${censusGender.malePct.toFixed(0)}% male. ${genderDelta != null && genderDelta > 0 ? `Over-indexes by ${genderDelta.toFixed(1)}pts.` : genderDelta != null ? `Under-indexes by ${Math.abs(genderDelta).toFixed(1)}pts.` : ''}`
              : hasCensus && censusGender
                ? `Census baseline · Male: ${censusGender.malePct.toFixed(1)}% · Female: ${censusGender.femalePct.toFixed(1)}%`
                : 'Needs demographics upload'}
          </div>
        </Card>

        {/* Avg Spend */}
        <Card>
          <div className={styles.insightKicker}>Avg Spend per Patient</div>
          <div className={hasFinancial ? styles.insightValueDefault : styles.insightValueDim}>
            {avgSpend != null ? `$${avgSpend.toLocaleString()}` : '—'}
          </div>
          {/* Production ÷ patients over whatever period the report covers —
              not necessarily a year, so it isn't labelled "/yr". */}
          {avgSpend != null && (
            <div className={styles.insightSub}>
              {spendPeriods > 1
                ? `Over the report's ${spendPeriods} periods`
                : "Over your report's period"}
            </div>
          )}
          {verticalId === 'dental' && (
            <div className={styles.insightSub}>National benchmark: {ADA_AVG_SPEND}</div>
          )}
        </Card>
      </div>
    </div>
  );
}
